import { useCallback, useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
  TextInput,
  SegmentedControl,
  NativeSelect,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import type { InteractionSummary } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { StatusBadge } from '../components/StatusBadge';
import { FormatGlyph } from '../components/FormatGlyph';
import { formatLabel, whenLabel } from '../format';

export function InteractionList() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const [items, setItems] = useState<readonly InteractionSummary[] | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('updated');
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setProblem(null);
    setItems(null);
    api
      .interactions(orgId)
      .then((body) => setItems(body.interactions))
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  const visible =
    items
      ?.filter(
        (item) =>
          (status === 'all' || item.status === status) &&
          `${item.title} ${formatLabel(item.format)}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
      )
      .sort((a, b) =>
        sort === 'title'
          ? a.title.localeCompare(b.title, 'en-GB')
          : b.updated_at.localeCompare(a.updated_at),
      ) ?? [];

  return (
    <>
      <Group className="library-heading" justify="space-between" align="center" gap="md" mb="md">
        <div>
          <Title order={1}>Interactions</Title>
          <Text c="dimmed" size="sm" mt="xs">
            Create, publish and keep the conversation going.
          </Text>
        </div>
        <Button
          component={Link}
          to="/interactions/new"
          leftSection={
            <span className="button-plus" aria-hidden="true">
              +
            </span>
          }
        >
          New interaction
        </Button>
      </Group>

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && items === null && <Loading label="Loading your interactions" />}

      {items !== null && items.length === 0 && <EmptyState />}

      {items !== null && items.length > 0 && (
        <>
          <div className="library-overview" aria-label="Interaction totals">
            {[
              {
                label: 'Total interactions',
                value: items.length,
                kind: 'total',
                note: 'Across your publisher',
              },
              {
                label: 'Published interactions',
                value: items.filter((item) => item.status === 'published').length,
                note: 'Available to readers',
                kind: 'published',
              },
              {
                label: 'Draft interactions',
                value: items.filter((item) => item.status === 'draft').length,
                note: 'Work in progress',
                kind: 'draft',
              },
            ].map((stat) => (
              <div className={`library-stat library-stat-${stat.kind}`} key={stat.label}>
                <span className="stat-marker" aria-hidden="true" />
                <div>
                  <Text size="xs" c="dimmed">
                    {stat.label}
                  </Text>
                  <Text className="stat-note" size="xs" c="dimmed">
                    {stat.note}
                  </Text>
                </div>
                <strong>{stat.value.toLocaleString('en-GB')}</strong>
              </div>
            ))}
          </div>
          <section className="library-panel" aria-label="Your interactions">
            <div className="library-panel-heading">
              <Text fw={600}>Your library</Text>
              <Text size="xs" c="dimmed" role="status">
                {visible.length} {visible.length === 1 ? 'interaction' : 'interactions'}
                {query.trim() ? ` matching “${query.trim()}”` : ''}
              </Text>
            </div>
            <div className="library-toolbar">
              <SegmentedControl
                aria-label="Filter by status"
                value={status}
                onChange={setStatus}
                data={[
                  { value: 'all', label: 'All' },
                  { value: 'published', label: 'Published' },
                  { value: 'draft', label: 'Drafts' },
                  { value: 'archived', label: 'Archived' },
                ]}
              />
              <div className="library-search-tools">
                <TextInput
                  aria-label="Search interactions"
                  placeholder="Search interactions…"
                  leftSection={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      aria-hidden="true"
                    >
                      <circle cx="10.5" cy="10.5" r="6.5" />
                      <path d="m16 16 4.5 4.5" />
                    </svg>
                  }
                  value={query}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                />
                <NativeSelect
                  aria-label="Sort interactions"
                  value={sort}
                  onChange={(event) => setSort(event.currentTarget.value)}
                  data={[
                    { value: 'updated', label: 'Last updated' },
                    { value: 'title', label: 'Title A–Z' },
                  ]}
                />
              </div>
            </div>
            {visible.length === 0 ? (
              <Card withBorder p="xl" className="library-no-results">
                <Title order={2} size="h3">
                  No matching interactions
                </Title>
                <Text size="sm" c="dimmed" mt="xs">
                  Try another title or choose a different status.
                </Text>
                <Button
                  variant="default"
                  mt="md"
                  onClick={() => {
                    setQuery('');
                    setStatus('all');
                  }}
                >
                  Clear filters
                </Button>
              </Card>
            ) : (
              <Stack component="ul" gap={0} className="interaction-library">
                {visible.map((item) => (
                  <Card
                    component="li"
                    key={item.interaction_id}
                    className="interaction-row"
                    radius={0}
                  >
                    <div className="interaction-row-main">
                      <FormatGlyph format={item.format} />
                      <div className="interaction-row-copy">
                        <Anchor
                          component={Link}
                          to={`/interactions/${item.interaction_id}`}
                          fw={500}
                          c="inherit"
                        >
                          {item.title}
                        </Anchor>
                        <Group gap="xs" mt={7}>
                          <Text size="xs" c="dimmed">
                            {formatLabel(item.format)}
                          </Text>
                          <Text size="xs" c="dimmed" aria-hidden="true">
                            ·
                          </Text>
                          <Text size="xs" c="dimmed">
                            Updated {whenLabel(item.updated_at)}
                          </Text>
                        </Group>
                      </div>
                      <StatusBadge status={item.status} />
                      <svg
                        className="row-arrow"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        aria-hidden="true"
                      >
                        <path d="M4 12h15m-5-5 5 5-5 5" />
                      </svg>
                    </div>
                    {item.consent_stale === true && (
                      <details className="consent-stale">
                        <summary>
                          <Badge size="sm" radius="sm" variant="light" color="coral" tt="none">
                            Older consent notice
                          </Badge>
                          <span>Review notice</span>
                        </summary>
                        <Text size="sm" c="dimmed">
                          Readers are seeing the consent notice this went out with, not the one its
                          brand shows now. Publishing it again brings it up to date.
                        </Text>
                      </details>
                    )}
                  </Card>
                ))}
              </Stack>
            )}
          </section>
        </>
      )}
    </>
  );
}

/**
 * The one place with any charm in it.
 *
 * An empty list is the only screen somebody sees once, so a drawing can sit there without becoming
 * wallpaper. Everywhere else it would be noise by the tenth visit.
 */
function EmptyState() {
  return (
    <Card withBorder radius="lg" p="xl" mt="lg">
      <Group align="flex-start" gap="lg" wrap="nowrap">
        <Box visibleFrom="sm" aria-hidden="true" style={{ flex: '0 0 auto' }}>
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none" role="presentation">
            <rect x="6" y="42" width="14" height="22" rx="4" fill="var(--mantine-color-sage-3)" />
            <rect
              x="26"
              y="26"
              width="14"
              height="38"
              rx="4"
              fill="var(--mantine-color-sage-filled)"
            />
            <rect x="46" y="14" width="14" height="50" rx="4" fill="var(--mantine-color-coral-4)" />
            <circle cx="53" cy="8" r="4" fill="var(--mantine-color-coral-filled)" />
          </svg>
        </Box>
        <div>
          <Title order={2} size="h3">
            Nothing here yet
          </Title>
          <Text mt="xs">
            A poll, a quiz, a survey or a form, ready to drop into an article. Make your first one,
            publish it, then paste the embed code into the article.
          </Text>
          <Button component={Link} to="/interactions/new" mt="md">
            New interaction
          </Button>
        </div>
      </Group>
    </Card>
  );
}
